// pages/app/Agents.tsx — mint / list / revoke per-agent MCP tokens (todo.md: per-agent identity).
// Scoped (read/write), optionally unit-type-constrained, rate-limited. The secret shows once.
import { useEffect, useState, type FormEvent } from 'react';
import { useServices } from '../../app/AppContext';
import type { AgentTokenInfo } from '../../lib/api';
import { ConfirmDialog, Dialog } from '../../components/Dialog';

const SCOPES = ['read', 'write'] as const;
const UNIT_TYPES = [
  'reasoning',
  'execution_step',
  'pattern',
  'principle',
  'knowledge',
  'memory',
] as const;

/** A pill toggle used for scopes + unit types (nicer than a checkbox). */
function Chip({
  label,
  aria,
  on,
  onClick,
}: {
  label: string;
  aria: string;
  on: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={aria}
      aria-pressed={on}
      onClick={onClick}
      className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition ${
        on
          ? 'border-brand bg-brand text-brand-fg shadow-brand-glow'
          : 'border-line/20 bg-surface-2 text-fg-muted hover:border-line/40 hover:text-fg'
      }`}
    >
      {label}
    </button>
  );
}

export function Agents() {
  const { api, config } = useServices();
  const [agents, setAgents] = useState<AgentTokenInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['read']);
  const [unitTypes, setUnitTypes] = useState<string[]>([]);
  const [rate, setRate] = useState(60);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [minted, setMinted] = useState<{ token: string; name: string } | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<AgentTokenInfo | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [copied, setCopied] = useState(false);

  const mcpUrl = `${config.siteUrl}/mcp`;

  async function copyToken(token: string) {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
    } catch {
      // clipboard unavailable — the token is shown for manual copy
    }
  }

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .listAgents()
      .then((r) => {
        if (active) {
          setAgents(r.agents);
          setError(null);
        }
      })
      .catch((e) => {
        if (active) setError(e instanceof Error ? e.message : 'Failed to load agents');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api, reloadKey]);

  function toggle(list: string[], set: (v: string[]) => void, value: string) {
    set(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);
  }

  async function mint(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.mintAgent({
        name: name.trim(),
        scopes,
        unitTypes,
        rateLimitPerMin: rate,
      });
      setMinted({ token: res.token, name: name.trim() });
      setName('');
      setScopes(['read']);
      setUnitTypes([]);
      setRate(60);
      setReloadKey((k) => k + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mint token');
    } finally {
      setBusy(false);
    }
  }

  async function confirmRevoke(target: AgentTokenInfo) {
    setRevoking(true);
    try {
      await api.revokeAgent(target.id);
      setPendingRevoke(null);
      setReloadKey((k) => k + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke');
    } finally {
      setRevoking(false);
    }
  }

  return (
    <div className="mx-auto max-w-5xl animate-fade-in space-y-6">
      <p className="text-sm text-fg-muted">
        Mint scoped, revocable tokens so external agents can reach your memory over MCP. The secret
        is shown once — store it safely.
      </p>

      <div className="card">
        <h2 className="text-base font-semibold">MCP endpoint</h2>
        <p className="mt-1 break-all font-mono text-xs text-fg-muted" data-testid="mcp-url">
          {mcpUrl}
        </p>
      </div>

      <div className="card space-y-3">
        <h2 className="text-base font-semibold">What your twin can do</h2>
        <p className="text-sm text-fg-muted">
          In chat, your twin uses tools to serve you and keep learning:
        </p>
        <ul className="space-y-1.5 text-sm text-fg-muted">
          <li>
            <span className="font-medium text-fg">Recall</span> — answers questions about you from
            your stored memory.
          </li>
          <li>
            <span className="font-medium text-fg">Remember</span> — deliberately captures durable
            facts you share (name, role, preferences, methods) — not blindly.
          </li>
          <li>
            <span className="font-medium text-fg">Search &amp; fetch</span> — looks things up on the
            web and reads a page you point it to.
          </li>
          <li>
            <span className="font-medium text-fg">Voice</span> — think-aloud recordings are analyzed
            by your Key1 model and distilled into memory.
          </li>
        </ul>
        <p className="text-sm text-fg-muted">
          External agents connect over MCP with: <code>recall</code>, <code>how_i_think</code>,{' '}
          <code>get_persona</code>, <code>upsert_memory</code>, <code>update_persona</code>,{' '}
          <code>contribute</code>.
        </p>
      </div>

      <form className="card space-y-5" onSubmit={mint}>
        <label className="block">
          <span className="field-label">Agent name</span>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Agent name"
            placeholder="e.g. Claude Desktop"
          />
        </label>

        <fieldset>
          <span className="field-label">Scopes</span>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {SCOPES.map((s) => (
              <Chip
                key={s}
                label={s}
                aria={`Scope ${s}`}
                on={scopes.includes(s)}
                onClick={() => toggle(scopes, setScopes, s)}
              />
            ))}
          </div>
        </fieldset>

        <fieldset>
          <span className="field-label">
            Unit types <span className="font-normal text-fg-subtle">(none = all)</span>
          </span>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {UNIT_TYPES.map((u) => (
              <Chip
                key={u}
                label={u.replace(/_/g, ' ')}
                aria={`Unit ${u}`}
                on={unitTypes.includes(u)}
                onClick={() => toggle(unitTypes, setUnitTypes, u)}
              />
            ))}
          </div>
        </fieldset>

        <label className="block">
          <span className="field-label">Rate limit (per minute)</span>
          <input
            type="number"
            min={1}
            max={1000}
            className="input"
            value={rate}
            onChange={(e) => setRate(Number(e.target.value))}
            aria-label="Rate limit"
          />
        </label>

        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}

        <button type="submit" className="btn" disabled={busy || !name.trim()}>
          {busy ? 'Minting…' : 'Mint token'}
        </button>
      </form>

      <section className="card">
        <h2 className="text-base font-semibold">Agent tokens</h2>
        {loading ? (
          <p className="mt-3 text-sm text-fg-muted">Loading…</p>
        ) : agents.length === 0 ? (
          <p className="mt-3 text-sm text-fg-muted">No agent tokens yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-line/10" data-testid="agents-list">
            {agents.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-fg">
                    {a.name}
                    {a.revokedAt ? <span className="ml-2 text-xs text-danger">revoked</span> : null}
                  </p>
                  <p className="truncate font-mono text-xs text-fg-muted">
                    {a.tokenPrefix}… · {a.scopes.join('/')} · {a.rateLimitPerMin}/min
                  </p>
                </div>
                {a.revokedAt ? null : (
                  <button
                    type="button"
                    className="btn-ghost text-sm text-danger"
                    aria-label={`Revoke ${a.name}`}
                    onClick={() => setPendingRevoke(a)}
                  >
                    Revoke
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <Dialog
        open={minted !== null}
        onClose={() => {
          setMinted(null);
          setCopied(false);
        }}
        title="Copy your agent token"
        footer={
          <button type="button" className="btn" onClick={() => setMinted(null)}>
            Done
          </button>
        }
      >
        <p>
          This is the only time <strong>{minted?.name}</strong>’s secret is shown. Store it now.
        </p>
        <div className="mt-3 flex items-stretch gap-2">
          <pre
            className="flex-1 overflow-x-auto rounded-lg bg-surface-2 p-3 font-mono text-xs"
            data-testid="minted-token"
          >
            {minted?.token}
          </pre>
          <button
            type="button"
            className="btn-secondary shrink-0"
            aria-label="Copy token"
            onClick={() => minted && void copyToken(minted.token)}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </Dialog>

      <ConfirmDialog
        open={pendingRevoke !== null}
        title="Revoke this token?"
        message={pendingRevoke ? `“${pendingRevoke.name}” will immediately lose access.` : ''}
        confirmLabel="Revoke"
        danger
        busy={revoking}
        onCancel={() => setPendingRevoke(null)}
        onConfirm={() => {
          if (pendingRevoke) void confirmRevoke(pendingRevoke);
        }}
      />
    </div>
  );
}
